/**
 * PHYSICAL-RISK-CARD.TSX
 * Risk summary card.
 */

import type { PreventionMeasuresView } from "@/entities/prevention-action/model/prevention-action.utils"
import Card from "@/components/card"
import { NEWLY_ADDED_RISK_PARAM, PhysicalRisk } from "../model/physical-risk.types"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import physicalRiskStore from "@/entities/physical-risk/model/physical-risk.store"
import PhysicalRiskEditModal from "./modal/PhysicalRiskEditModal"
import { MouseEvent, useState } from "react"
import preventionActionStore from "@/entities/prevention-action/model/prevention-action.store"
import PreventionActionsChecklist from "@/entities/prevention-action/ui/PreventionActionsChecklist"
import RiskHeader from "./RiskHeader"
import { WizardStep } from "./wizard/physical-risk-wizard.constants"
import PreventionActionAddModal from "@/entities/prevention-action/ui/modal/PreventionActionAddModal"
import { useSearchParams } from "react-router-dom"
import NewlyAddedHighlight from "@/components/NewlyAddedHighlight"
import { useRisksSpace } from "@/core/risks-space.context"
import { isInteractiveTarget, usePhysicalRiskSelection } from "./PhysicalRiskSelection"

interface OwnProps {
  isWithoutMeasures?: boolean //Sans la checklist des mesures (ex : vue d'ensemble de l'etablissement)
  measuresView?: PreventionMeasuresView
  risk: PhysicalRisk
}

export default function PhysicalRiskCard(props: OwnProps) {
  const dispatch = useAppDispatch()
  const [searchParams] = useSearchParams()
  const { isReadonly } = useRisksSpace()
  const riskSelection = usePhysicalRiskSelection()
  const selection = isReadonly ? null : riskSelection

  function toggleSelection(event: MouseEvent<HTMLDivElement>) {
    // Les actions internes (et leurs modales rendues dans un portail) gardent leur propre clic.
    const target = event.target as Element
    if (!event.currentTarget.contains(target) || isInteractiveTarget(target)) return
    selection?.toggle(props.risk.id)
  }
  //risk : la modale repart de l'objet recu, on y injecte donc le niveau de prevention suggere
  const [editModal, setEditModal] = useState<null | { initialStep?: WizardStep; risk: PhysicalRisk }>(null)
  const [isAddPreventionModalOpen, setIsAddPreventionModalOpen] = useState(false)
  const actionsForRisk = useAppSelector((state) => preventionActionStore.selectors.listForRisk(state, props.risk.id))
  //Le repere vit dans l'URL : il s'affiche tant qu'on reste sur la page et disparait des qu'on la quitte.
  const isNewlyAdded = searchParams.get(NEWLY_ADDED_RISK_PARAM) === props.risk.id

  function handleClick() {
    dispatch(physicalRiskStore.actions.setActive(props.risk))
    setEditModal({ risk: props.risk })
  }

  //Mesure cochee "faite" : le niveau de prevention se confirme dans le wizard, sur l'etape Evaluation
  function handleReviewPrevention(suggestedPreventionValue: number) {
    setEditModal({ initialStep: "EVALUATION", risk: { ...props.risk, prevention: suggestedPreventionValue } })
  }

  function handleAddPrevention() {
    dispatch(physicalRiskStore.actions.setActive(props.risk))
    setIsAddPreventionModalOpen(true)
  }

  return (
    <>
      {editModal && (
        <PhysicalRiskEditModal
          risk={editModal.risk}
          initialStep={editModal.initialStep}
          onClose={() => setEditModal(null)}
        />
      )}
      {isAddPreventionModalOpen && <PreventionActionAddModal onClose={() => setIsAddPreventionModalOpen(false)} />}
      <NewlyAddedHighlight className="mt-20" isActive={isNewlyAdded} label="Risque ajouté">
        <Card
          isWithoutPadding
          isSelected={selection ? selection.selectedIds.includes(props.risk.id) : undefined}
          onClick={selection ? toggleSelection : undefined}
          className="mb-0 px-[30px] py-[20px]">
          <div className={props.isWithoutMeasures ? "" : "mb-[10px]"}>
            <RiskHeader
              isSummary={props.isWithoutMeasures}
              risk={props.risk}
              onRiskClick={handleClick}
              onAddPrevention={isReadonly ? undefined : handleAddPrevention}
            />
          </div>

          {!props.isWithoutMeasures && (
            <div className="mb-10">
              <PreventionActionsChecklist
                isReadonly={isReadonly}
                preventionActions={actionsForRisk}
                measuresView={props.measuresView}
                onReviewPrevention={isReadonly ? undefined : handleReviewPrevention}
              />
            </div>
          )}
        </Card>
      </NewlyAddedHighlight>
    </>
  )
}
